import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { Alert, Card, Col, DatePicker, Row, Segmented, Select, Space, Statistic, Typography, theme } from 'antd'
import type { GlobalToken } from 'antd'
import { DollarOutlined, ExclamationCircleOutlined, WalletOutlined } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import { type Dayjs } from 'dayjs'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { translateError } from '@/shared/api/errors'
import { queryKeys } from '@/shared/api/queryKeys'
import { tenantToday } from '@/shared/lib/dayRange'
import { formatMoneyValue } from '@/shared/lib/money'
import {
  ISO_DAY,
  fetchFinanceDashboard,
  periodRange,
  type DashboardFilter,
  type DashboardMetric,
  type DashboardPeriod,
} from '@/features/reports/financeDashboardApi'
import { useBranchOptions } from '@/features/reports/useBranchOptions'
import { useActiveBranches } from '@/features/branch/useActiveBranches'

const { RangePicker } = DatePicker

const CARDS: { metric: DashboardMetric; key: string; icon: ReactNode; accent: (token: GlobalToken) => string }[] = [
  { metric: 'totalSales', key: 'sales', icon: <DollarOutlined />, accent: (token) => token.blue },
  { metric: 'totalCollected', key: 'collected', icon: <WalletOutlined />, accent: (token) => token.colorSuccess },
  { metric: 'outstanding', key: 'outstanding', icon: <ExclamationCircleOutlined />, accent: (token) => token.colorError },
]


export function SalesCollectionsPanel({ refreshMs }: { refreshMs: number }) {
  const { t } = useTranslation()
  const { token } = theme.useToken()
  const [period, setPeriod] = useState<DashboardPeriod>('all')
  const [custom, setCustom] = useState<[Dayjs, Dayjs]>(() => [tenantToday().subtract(6, 'day'), tenantToday()])
  const { selected: activeBranches } = useActiveBranches()
  const [branchIds, setBranchIds] = useState<string[]>(() => [...activeBranches])
  const activeKey = activeBranches.join()
  useEffect(() => {
    setBranchIds(activeKey ? activeKey.split(',') : [])
  }, [activeKey])
  const branches = useBranchOptions()

  const range = periodRange(period, custom)
  const from = range ? range[0].format(ISO_DAY) : null
  const to = range ? range[1].format(ISO_DAY) : null
  const filter: DashboardFilter = useMemo(() => ({ from, to, branchIds }), [from, to, branchIds])

  const dashboard = useQuery({
    queryKey: queryKeys.financeDashboard(filter),
    queryFn: ({ signal }) => fetchFinanceDashboard(filter, signal),
    refetchInterval: refreshMs,
  })
  const data = dashboard.data

  return (
    <Card
      size="small"
      style={{ marginBottom: 16 }}
      title={t('dashboard.money.title')}
      extra={<Link to="/finance/dashboard">{t('dashboard.money.open')}</Link>}
    >
      <Space wrap style={{ marginBottom: 12 }}>
        <Segmented<DashboardPeriod>
          value={period}
          onChange={setPeriod}
          options={[
            { value: 'all', label: t('financeDashboard.period.all') },
            { value: 'today', label: t('financeDashboard.period.today') },
            { value: 'month', label: t('financeDashboard.period.month') },
            { value: 'custom', label: t('financeDashboard.period.custom') },
          ]}
        />
        {period === 'custom' ? (
          <RangePicker
            value={custom}
            allowClear={false}
            disabledDate={(day) => day.isAfter(tenantToday(), 'day')}
            onChange={(value) => {
              if (value?.[0] && value[1]) setCustom([value[0], value[1]])
            }}
          />
        ) : null}
        {branches.length > 1 ? (
          <Select<string[]>
            mode="multiple"
            allowClear
            maxTagCount="responsive"
            optionFilterProp="label"
            style={{ minWidth: 240 }}
            placeholder={t('financeDashboard.filter.allBranches')}
            value={branchIds}
            onChange={setBranchIds}
            options={branches}
          />
        ) : null}
      </Space>

      {dashboard.isError ? (
        <Alert type="error" showIcon style={{ marginBottom: 12 }} message={translateError(dashboard.error, t)} />
      ) : null}

      <Row gutter={[16, 16]}>
        {CARDS.map(({ metric, key, icon, accent }) => (
          <Col key={metric} xs={24} sm={12} lg={8}>
            <Card
              size="small"
              loading={dashboard.isPending}
              style={{ height: '100%', borderTop: `3px solid ${accent(token)}` }}
            >
              <Statistic
                title={t(`dashboard.money.${key}`)}
                value={data ? formatMoneyValue(data.metrics[metric]) : '—'}
                prefix={<span style={{ color: accent(token) }}>{icon}</span>}
              />
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                {t(`financeDashboard.hint.${metric}`)}
              </Typography.Text>
            </Card>
          </Col>
        ))}
      </Row>

      {data?.restricted ? (
        <Typography.Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
          {t('financeDashboard.restricted')}
        </Typography.Text>
      ) : null}
    </Card>
  )
}
